import { useCallback, useState } from 'react'
import { Row, Col } from 'antd'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { Season, SeasonLot } from 'api'
import { FieldsSelect, ListCard } from 'components'
import { ActiveCompanyContext } from 'contexts'
import { LotsMapLocationState } from 'types'
import { usePermissions } from 'hooks'
import type { SelectField } from 'components/fields-selects/types'

import { useDeleteLot } from '../../../../hooks'
import { DeleteLotConfirmationModal } from '../../../../components'
import { IrrigationWorkersSelect } from './components'

const FieldsSelectContainer = styled(Row)`
  margin-bottom: 24px;
`

interface Props {
  season: Season
}

export const SeasonLots: React.FC<Props> = ({ season }) => {
  const { activeCompanyId } = ActiveCompanyContext.useActiveCompanyContext()
  const navigate = useNavigate()
  const { permissions } = usePermissions()
  const { t } = useTranslation('season-settings')
  const { t: commonT } = useTranslation('common')

  const [selectedFieldId, setSelectedFieldId] = useState<number>()
  const [selectedLotToDelete, setSelectedLotToDelete] = useState<SeasonLot>()
  const [showDeleteLotModal, setShowDeleteLotModal] = useState(false)

  const { deleteLot, loading: deleteLotLoading } = useDeleteLot(() => {
    setSelectedLotToDelete(undefined)
    setShowDeleteLotModal(false)
  })

  const selectedSeasonField = season.seasonFields.find(
    seasonField => seasonField.field?.id === selectedFieldId,
  )

  const onDeleteLot = (id: number) => {
    const selectedLot = selectedSeasonField?.lots.find(lot => lot.id === id)
    if (!selectedLot) return
    setSelectedLotToDelete(selectedLot)
    setShowDeleteLotModal(true)
  }

  const onConfirmDeleteLot = () => {
    if (!selectedLotToDelete) return
    deleteLot({ variables: { id: selectedLotToDelete.id } })
  }

  const onCancelDeleteLot = () => {
    setSelectedLotToDelete(undefined)
    setShowDeleteLotModal(false)
  }

  const handleChange = useCallback((newField: SelectField) => {
    setSelectedFieldId(newField.id)
  }, [])

  const selectedFieldLots = selectedSeasonField?.lots.map(lot => ({
    ...lot,
    isDisabled: lot.riceLot.isPlanetEnabled,
  }))

  return (
    <>
      <ListCard
        data={selectedFieldLots ?? []}
        isAdminUser={permissions.isCompanyAdminUser}
        title={t('lotsCard.title', { context: permissions.fieldEntity && 'withFieldEntity' })}
        description={t('lotsCard.description', {
          context: permissions.fieldEntity && 'withFieldEntity',
        })}
        header={
          <>
            {permissions.isAnOrganizationUser ? (
              <Row>
                <Col span={8}>{t('lotsCard.table.lotText')}</Col>
                {permissions.isCommercialNameEnabled && (
                  <Col span={4}>{t('lotsCard.table.commercialName')}</Col>
                )}
                {permissions.satelliteImagery && <Col>{t('lotsCard.table.irrigationWorker')}</Col>}
              </Row>
            ) : (
              <Row>
                <Col span={8}>{t('lotsCard.table.fieldText')}</Col>
                <Col span={4}>{t('lotsCard.table.lotText')}</Col>
                {permissions.satelliteImagery && <Col>{t('lotsCard.table.irrigationWorker')}</Col>}
              </Row>
            )}
          </>
        }
        headerSubsection={
          // For now we just hide the FieldsSelect so logic behind the scenes still works and the
          // first and only field (only one when flag fieldEntity is off) gets selected by default
          <FieldsSelectContainer hidden={!permissions.fieldEntity}>
            <FieldsSelect companyId={activeCompanyId} onChange={handleChange} />
          </FieldsSelectContainer>
        }
        buttonDescription={t('lotsCard.table.addLotsButtonText')}
        onClick={() =>
          navigate('/settings/lots', {
            state: { seasonId: season.id, fieldId: selectedFieldId } as LotsMapLocationState,
          })
        }
        onEdit={id =>
          navigate(`/settings/lots`, {
            state: {
              seasonId: season.id,
              fieldId: selectedFieldId,
              lotId: id,
            } as LotsMapLocationState,
          })
        }
        onDelete={onDeleteLot}
        renderItem={item => (
          <>
            {permissions.isAnOrganizationUser ? (
              <>
                <Col span={8}>{item.name}</Col>
                {permissions.isCommercialNameEnabled && <Col span={4}>{item.commercialName}</Col>}
                {permissions.satelliteImagery && permissions.isCompanyAdminUser && (
                  <Col>
                    <IrrigationWorkersSelect
                      lotId={item.id}
                      selectedValue={item.irrigationWorker?.id}
                      companyId={activeCompanyId}
                    />
                  </Col>
                )}
              </>
            ) : (
              <>
                <Col span={8}>{selectedSeasonField?.field?.name}</Col>
                <Col span={4}>{item.name}</Col>
                {permissions.satelliteImagery && (
                  <Col>
                    <IrrigationWorkersSelect
                      lotId={item.id}
                      selectedValue={item.irrigationWorker?.id}
                      companyId={activeCompanyId}
                    />
                  </Col>
                )}
              </>
            )}
          </>
        )}
        disabledTooltipText={commonT('messages.highFrequencyLot')}
      />
      {showDeleteLotModal && selectedLotToDelete && selectedSeasonField && (
        <DeleteLotConfirmationModal
          lotName={selectedLotToDelete.name}
          fieldName={selectedSeasonField.field!.name}
          onConfirm={onConfirmDeleteLot}
          onCancel={onCancelDeleteLot}
          loading={deleteLotLoading}
        />
      )}
    </>
  )
}
